<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script>
import { range } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { arc, line } from 'd3-shape';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { colormaps } from '@/src/components/analysis/colormaps';
import ScreenshotDialog from '@/src/components/ScreenshotDialog.vue';
import { useImageClipboardStore } from '@/src/store/image-clipboard-store';

// Example default data
const defaultRings = [
  [0.1, 0.3, 0.5, 0.7, 0.9, 1.0, 0.9, 0.7, 0.5, 0.3, 0.1, 0.0],
  [0.9, 0.7, 0.5, 0.3, 0.1, 0.0, 0.1, 0.3, 0.5, 0.7, 0.9, 1.0],
  [0.2, 0.4, 0.6, 0.8, 1.0, 0.9, 0.8, 0.6, 0.4, 0.2, 0.0, 0.1],
];

const interpolationModes = [
  { value: 'none', label: 'None' },
  { value: 'circumferential', label: 'Circumferential' },
  { value: 'radial', label: 'Radial' },
  { value: 'both', label: 'Both' },
];

const gridTypes = {
  none: 'None',
  data: 'Match Data',
  aha: 'AHA',
  territories: 'Territories',
};

const colormapLabels = {
  'plasma': 'Plasma',
  'viridis': 'Viridis',
  'turbo': 'Turbo',
  'magma': 'Magma',
  'inferno': 'Inferno',
  'cividis': 'Cividis',
  'red-yellow-blue': 'Red-Yellow-Blue',
  'red-blue': 'Red-Blue',
};

// Sentinel returned for pixels that fall inside the donut but have no data
const NO_DATA = Symbol('no-data');

// A cell is "no data" if it is null/undefined/NaN
const isMissing = val =>
  val === null || val === undefined || Number.isNaN(val);

// Safe cell access; returns undefined for empty/short rings
const cell = (rings, i, j) => {
  const ring = rings[i];
  if (!ring || ring.length === 0) return undefined;
  return ring[j];
};

// First populated ring index, or -1 if all rings are empty
const firstNonEmptyIndex = rings =>
  rings.findIndex(ring => ring && ring.length > 0);

export default {
  name: 'Polarmap',
  components: {
    ScreenshotDialog,
  },
  props: {
    ringsData: {
      type: Array,
      default: () => defaultRings,
      validator: (rings) => {
        // Check if array is empty
        if (!rings.length) return false;

        // Find first and last non-empty ring
        const firstNonEmpty = rings.findIndex(ring => ring.length > 0);
        if (firstNonEmpty === -1) return true; // All empty is valid

        const lastNonEmpty
          = rings.length
            - 1
            - [...rings].reverse().findIndex(ring => ring.length > 0);

        // Get the expected number of segments from first non-empty ring
        const expectedSegments = rings[firstNonEmpty].length;

        // Check that all rings between first and last non-empty:
        // 1. Are non-empty
        // 2. Have the same number of segments
        return rings
          .slice(firstNonEmpty, lastNonEmpty + 1)
          .every(ring => ring.length === expectedSegments);
      },
    },
    colormap: {
      type: String,
      default: 'plasma',
      validator: v => Object.keys(colormaps).includes(v),
    },
    innerCircleRadius: { type: Number, default: 0.2 },
    interpolationMode: {
      type: String,
      default: 'both',
      validator: v =>
        ['none', 'circumferential', 'radial', 'both'].includes(v),
    },
    pixelSize: { type: Number, default: 1 },
    title: { type: String, default: 'Polarmap' },
    unit: { type: String, default: '%' },
    description: { type: String, default: '' },
    highlightSlice: { type: Number, default: -1 }, // -1 = no marker
  },
  emits: ['update:interpolationMode', 'update:colormap'],
  setup(props, { emit }) {
    const canvas = ref(null);
    const startAngleOffset = ref(-30);
    const currentInterpolationMode = ref(props.interpolationMode);
    const currentColormap = ref(props.colormap);
    const currentGridType = ref('data');
    const showMenu = ref(false);
    const menuStyle = ref({
      top: '0px',
      left: '0px',
    });
    const paddingX = ref(60);
    const paddingY = ref(10);

    // Add tooltip refs
    const showTooltip = ref(false);
    const tooltipValue = ref(0);
    const tooltipSlice = ref(0);
    const tooltipSegment = ref(0);
    const tooltipStyle = ref({
      top: '0px',
      left: '0px',
    });

    let ctx;
    let resizeObserver;

    // Calculate mean and standard deviation once (ignoring missing cells)
    const statsCalculation = computed(() => {
      const values = props.ringsData.flat().filter(v => !isMissing(v));
      if (values.length === 0) return { mean: 0, stdDev: 0 };

      // Calculate mean
      const sum = values.reduce((acc, val) => acc + val, 0);
      const mean = sum / values.length;

      // Calculate standard deviation
      const squaredDiffs = values.map(val => (val - mean) ** 2);
      const variance
        = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
      const stdDev = Math.sqrt(variance);

      return { mean, stdDev };
    });

    // Calculate mean - 2 standard deviations
    const meanMinus2Std = computed(() => {
      return statsCalculation.value.mean - 2 * statsCalculation.value.stdDev;
    });

    // Calculate mean + 2 standard deviations
    const meanPlus2Std = computed(() => {
      return statsCalculation.value.mean + 2 * statsCalculation.value.stdDev;
    });

    const minValue = computed(() => {
      return meanMinus2Std.value;
    });
    const maxValue = computed(() => {
      return meanPlus2Std.value;
    });

    // Build a diagonal-hatch pattern (with a muted base) so empty rings /
    // missing cells are clearly readable as "no data". The pattern is anchored
    // to canvas space, so it stays continuous across the small fillRect() calls.
    const makeHatchPattern = (context) => {
      const tile = document.createElement('canvas');
      tile.width = 6;
      tile.height = 6;
      const t = tile.getContext('2d');
      t.fillStyle = 'rgba(90, 90, 90, 0.35)';
      t.fillRect(0, 0, 6, 6);
      t.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      t.lineWidth = 1;
      t.beginPath();
      t.moveTo(0, 6);
      t.lineTo(6, 0);
      t.moveTo(-2, 2);
      t.lineTo(2, -2);
      t.moveTo(4, 8);
      t.lineTo(8, 4);
      t.stroke();
      return context.createPattern(tile, 'repeat');
    };

    function computePolarmapValue(
      x,
      y,
      rings,
      innerR,
      outerR,
      cx,
      cy,
      startDeg,
      mode,
      S,
    ) {
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      if (r < innerR || r > outerR) return null;

      const R = rings.length;
      if (R === 0 || S === 0) return null;

      // Calculate angle in radians, with 0 at 12 o'clock
      let theta = Math.atan2(dx, -dy); // 0° at top

      // Apply start angle offset (converting from degrees to radians)
      theta -= (startAngleOffset.value * Math.PI) / 180;

      // Normalize to [0, 2π]
      theta = ((theta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

      // Calculate segment parameters, with offset only for circumferential interp
      const segmentWidth = (2 * Math.PI) / S;
      const thetaAdjusted
        = mode === 'circumferential' || mode === 'both'
          ? theta - segmentWidth / 2
          : theta;

      const thetaNorm
        = ((thetaAdjusted % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const segF = (thetaNorm / (2 * Math.PI)) * S;

      const si = Math.floor(segF) % S;
      const st = segF - Math.floor(segF);

      // Linear mapping for slice boundaries
      const radialFraction = (r - innerR) / (outerR - innerR);

      // Nearest ring band (reversed mapping: outer radius -> index 0)
      const bandIdx = Math.min(
        R - 1,
        Math.max(0, Math.floor((1 - radialFraction) * R)),
      );

      // Dominant cell drives the no-data test, so hatched regions line up
      // with the segment grid rather than smearing through interpolation.
      const siNearest
        = mode === 'circumferential' || mode === 'both'
          ? Math.round(segF) % S
          : si;
      const dominant = cell(rings, bandIdx, siNearest);
      if (isMissing(dominant)) return NO_DATA;

      // Handle ring indexing differently for radial vs non-radial modes
      let ri;
      let rt;
      if ((mode === 'radial' || mode === 'both') && R >= 2) {
        // Extend fraction range by 30% on each end for border interpolation
        const extendedFraction = radialFraction * 1.3 - 0.15;
        // REVERSED: Map high values (outer radius) to low indices (0)
        const radF = (1 - extendedFraction) * (R - 1);
        ri = Math.min(R - 2, Math.max(0, Math.floor(radF)));
        rt = radF - ri;
      }
      else {
        ri = bandIdx;
        rt = 0; // No radial interpolation
      }

      // Ensure we don't go out of bounds
      const i0 = ri;
      const i1 = Math.min(R - 1, ri + 1);
      const j0 = si;
      const j1 = (si + 1) % S;

      // Backfill missing neighbours with the dominant value (never blend NaN)
      const at = (i, j) => {
        const v = cell(rings, i, j);
        return isMissing(v) ? dominant : v;
      };

      const v00 = at(i0, j0);
      const v10 = at(i0, j1);
      const v01 = at(i1, j0);
      const v11 = at(i1, j1);

      // Handle the last segment specially for circumferential interpolation
      if ((mode === 'circumferential' || mode === 'both') && j1 === 0) {
        const stAdjusted = Math.min(1, Math.max(0, st));
        if (mode === 'circumferential') {
          return v00 + (v10 - v00) * stAdjusted;
        }
        const m0 = v00 + (v10 - v00) * stAdjusted;
        const m1 = v01 + (v11 - v01) * stAdjusted;
        return m0 + (m1 - m0) * rt;
      }

      switch (mode) {
        case 'none':
          return at(i0, j0);
        case 'circumferential':
          return v00 + (v10 - v00) * st;
        case 'radial':
          return v00 + (v01 - v00) * rt;
        case 'both': {
          const m0 = v00 + (v10 - v00) * st;
          const m1 = v01 + (v11 - v01) * st;
          return m0 + (m1 - m0) * rt;
        }
        default:
          return at(i0, j0);
      }
    }

    const getColor = (value) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return 'rgba(0, 0, 0, 0)'; // Transparent
      }
      const t = (value - minValue.value) / (maxValue.value - minValue.value);
      const u = Math.max(0, Math.min(1, t));
      return colormaps[currentColormap.value](u);
    };

    // Function to draw AHA grid
    const drawAHAGrid = (gridGroupAll, outerR, innerR) => {
      // Three rings with different radii
      const ringRadii = [
        innerR,
        innerR + (outerR - innerR) * 0.33,
        innerR + (outerR - innerR) * 0.66,
        outerR,
      ];

      gridGroupAll.selectAll('path.circumferential-line').remove();

      // Draw ring circles
      gridGroupAll
        .selectAll('circle.ring-boundary')
        .data(ringRadii)
        .join('circle')
        .classed('ring-boundary', true)
        .attr('r', d => d)
        .attr('fill', 'none')
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 2);

      // Draw outer and middle segments (6 segments, both starting at 11 o'clock)
      const outerSegments = [];
      const middleSegments = [];

      for (let i = 0; i < 6; i++) {
        const angle = (i * 2 * Math.PI) / 6 - Math.PI / 2 - Math.PI / 6;

        outerSegments.push([
          [ringRadii[2] * Math.cos(angle), ringRadii[2] * Math.sin(angle)],
          [outerR * Math.cos(angle), outerR * Math.sin(angle)],
        ]);
        middleSegments.push([
          [ringRadii[1] * Math.cos(angle), ringRadii[1] * Math.sin(angle)],
          [ringRadii[2] * Math.cos(angle), ringRadii[2] * Math.sin(angle)],
        ]);
      }

      // Draw inner segments (4 segments starting at 10:30)
      const innerSegments = range(4).map((i) => {
        const angle = (i * 2 * Math.PI) / 4 - Math.PI / 2 - Math.PI / 4;
        return [
          [innerR * Math.cos(angle), innerR * Math.sin(angle)],
          [ringRadii[1] * Math.cos(angle), ringRadii[1] * Math.sin(angle)],
        ];
      });

      const lineGenerator = line()
        .x(d => d[0])
        .y(d => d[1]);

      gridGroupAll
        .selectAll('path.radial-line')
        .data([...outerSegments, ...middleSegments, ...innerSegments])
        .join('path')
        .classed('radial-line', true)
        .attr('d', lineGenerator)
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 2)
        .attr('fill', 'none');
    };

    // Function to draw data-matching grid
    const drawDataGrid = (gridGroupAll, outerR, innerR) => {
      const firstNonEmpty = firstNonEmptyIndex(props.ringsData);
      const S = firstNonEmpty === -1 ? 0 : props.ringsData[firstNonEmpty].length;
      const R = props.ringsData.length;

      gridGroupAll.selectAll('path.circumferential-line').remove();

      if (S === 0 || R === 0) {
        gridGroupAll.selectAll('circle.ring-boundary').remove();
        gridGroupAll.selectAll('path.radial-line').remove();
        return;
      }

      // Draw ring circles; dash boundaries that touch an empty ring
      const ringRadii = range(R + 1).map(i => ({
        radius: innerR + (outerR - innerR) * (i / R),
        // ring index just inside/outside this boundary (reversed mapping)
        empty:
          (props.ringsData[R - i] && props.ringsData[R - i].length === 0)
          || (props.ringsData[R - i - 1]
            && props.ringsData[R - i - 1].length === 0),
      }));

      gridGroupAll
        .selectAll('circle.ring-boundary')
        .data(ringRadii)
        .join('circle')
        .classed('ring-boundary', true)
        .attr('r', d => d.radius)
        .attr('fill', 'none')
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', d => (d.empty ? '4 3' : null));

      // Draw radial lines
      const angleStep = (2 * Math.PI) / S;
      const radialLines = range(S).map((i) => {
        const angle = i * angleStep;
        return [
          [innerR * Math.cos(angle), innerR * Math.sin(angle)],
          [outerR * Math.cos(angle), outerR * Math.sin(angle)],
        ];
      });

      const lineGenerator = line()
        .x(d => d[0])
        .y(d => d[1]);

      gridGroupAll
        .selectAll('path.radial-line')
        .data(radialLines)
        .join('path')
        .classed('radial-line', true)
        .attr('d', lineGenerator)
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 2)
        .attr('fill', 'none');
    };

    // Function to draw territories grid
    const drawTerritoriesGrid = (gridGroupAll, outerR, innerR) => {
      const oneThirdR = innerR + (outerR - innerR) * 0.333;

      gridGroupAll.selectAll('circle.ring-boundary').remove();
      const oneOClock = -Math.PI / 3;
      const fiveOClock = Math.PI / 3;
      const nineOClock = -Math.PI;
      const oneThirty = -Math.PI / 4;
      const sevenThirty = (-5 * Math.PI) / 4;
      const fourThirty = Math.PI / 4;

      const radialLines = [
        [
          [outerR * Math.cos(nineOClock), outerR * Math.sin(nineOClock)],
          [oneThirdR * Math.cos(nineOClock), oneThirdR * Math.sin(nineOClock)],
        ],
        [
          [innerR * Math.cos(sevenThirty), innerR * Math.sin(sevenThirty)],
          [
            oneThirdR * Math.cos(sevenThirty),
            oneThirdR * Math.sin(sevenThirty),
          ],
        ],
        [
          [outerR * Math.cos(oneOClock), outerR * Math.sin(oneOClock)],
          [oneThirdR * Math.cos(oneOClock), oneThirdR * Math.sin(oneOClock)],
        ],
        [
          [innerR * Math.cos(oneThirty), innerR * Math.sin(oneThirty)],
          [oneThirdR * Math.cos(oneThirty), oneThirdR * Math.sin(oneThirty)],
        ],
        [
          [outerR * Math.cos(fiveOClock), outerR * Math.sin(fiveOClock)],
          [oneThirdR * Math.cos(fiveOClock), oneThirdR * Math.sin(fiveOClock)],
        ],
        [
          [innerR * Math.cos(fourThirty), innerR * Math.sin(fourThirty)],
          [oneThirdR * Math.cos(fourThirty), oneThirdR * Math.sin(fourThirty)],
        ],
      ];

      const lineGenerator = line()
        .x(d => d[0])
        .y(d => d[1]);

      gridGroupAll
        .selectAll('path.radial-line')
        .data(radialLines)
        .join('path')
        .classed('radial-line', true)
        .attr('d', lineGenerator)
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 2)
        .attr('fill', 'none');

      const arc1Generator = arc()
        .innerRadius(d => d)
        .outerRadius(d => d)
        .startAngle(fourThirty + Math.PI / 2)
        .endAngle(fiveOClock + Math.PI / 2);

      const arc2Generator = arc()
        .innerRadius(d => d)
        .outerRadius(d => d)
        .startAngle(oneOClock + Math.PI / 2)
        .endAngle(oneThirty + Math.PI / 2);

      const arc3Generator = arc()
        .innerRadius(d => d)
        .outerRadius(d => d)
        .startAngle(sevenThirty + Math.PI / 2)
        .endAngle(nineOClock + Math.PI / 2);

      const arcGenerators = [arc1Generator, arc2Generator, arc3Generator];

      arcGenerators.forEach((generator, i) => {
        gridGroupAll
          .selectAll(`path.circumferential-line-${i}`)
          .data([oneThirdR])
          .join('path')
          .classed(`circumferential-line-${i}`, true)
          .classed('circumferential-line', true)
          .attr('d', generator)
          .attr('stroke', 'rgba(255,255,255,0.3)')
          .attr('stroke-width', 2)
          .attr('fill', 'none');
      });
    };

    const draw = () => {
      const c = canvas.value;
      if (!c) return;
      const parent = c.parentElement;
      c.width = parent.clientWidth;
      c.height = parent.clientHeight;
      ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);

      if (props.ringsData.length === 0) {
        return;
      }

      // Canonical segment count from the first NON-empty ring (not rings[0])
      const firstNonEmpty = firstNonEmptyIndex(props.ringsData);
      const S
        = firstNonEmpty === -1 ? 0 : props.ringsData[firstNonEmpty].length;

      const cx = (c.width - paddingX.value) / 2;
      const cy = (c.height - paddingY.value) / 2;
      const outerR = Math.min(cx, cy);
      const innerR = outerR * props.innerCircleRadius;
      if (outerR <= 0) return; // container has no usable size yet

      // Create SVG overlay if it doesn't exist
      let svg = select(parent).select('svg');
      if (svg.empty()) {
        svg = select(parent)
          .append('svg')
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0')
          .style('pointer-events', 'none');
      }

      svg.attr('width', c.width).attr('height', c.height);

      // Colorbar legend
      const legendWidth = 20;
      const legendHeight = Math.min(200, c.height * 0.5);
      const legendX = c.width - legendWidth - 20;
      const legendY = c.height / 2 - legendHeight / 2;
      const gradientId = 'colorbar-gradient';

      let gradient = svg.select(`#${gradientId}`);
      if (gradient.empty()) {
        gradient = svg
          .append('defs')
          .append('linearGradient')
          .attr('id', gradientId)
          .attr('gradientUnits', 'userSpaceOnUse');
      }

      gradient
        .attr('x1', 0)
        .attr('y1', legendY + legendHeight)
        .attr('x2', 0)
        .attr('y2', legendY)
        .selectAll('stop')
        .data(Array.from({ length: 10 }, (_, i) => i / 9))
        .join('stop')
        .attr('offset', d => `${d * 100}%`)
        .attr('stop-color', d => colormaps[currentColormap.value](d));

      const legendGroup = svg.selectAll('g.colorbar').data([null]);
      const legendGroupEnter = legendGroup
        .enter()
        .append('g')
        .classed('colorbar', true);

      const legendGroupAll = legendGroup.merge(legendGroupEnter);

      legendGroupAll
        .selectAll('rect.colorbar')
        .data([null])
        .join('rect')
        .classed('colorbar', true)
        .attr('x', legendX)
        .attr('y', legendY)
        .attr('width', legendWidth)
        .attr('height', legendHeight)
        .attr('fill', `url(#${gradientId})`)
        .attr('stroke', 'white')
        .attr('stroke-width', 1);

      const valueScale = scaleLinear()
        .domain([maxValue.value, minValue.value])
        .range([legendY, legendY + legendHeight]);

      legendGroupAll
        .selectAll('text.legend-label')
        .data([minValue.value, maxValue.value])
        .join('text')
        .classed('legend-label', true)
        .attr('x', legendX + legendWidth)
        .attr('y', d =>
          d === maxValue.value ? valueScale(d) : valueScale(d) + 10)
        .attr('dy', d => (d === maxValue.value ? '-0.5em' : '0'))
        .attr('fill', 'white')
        .attr('font-size', '12px')
        .attr('font-family', 'sans-serif')
        .attr('text-anchor', 'end')
        .attr('alignment-baseline', d =>
          d === maxValue.value ? 'auto' : 'middle')
        .text(d => d.toFixed(1));

      // Circles + marker
      const circleGroup = svg.selectAll('g.circles').data([null]);
      const circleGroupEnter = circleGroup
        .enter()
        .append('g')
        .classed('circles', true)
        .attr('transform', `translate(${cx},${cy})`);

      const circleGroupAll = circleGroup
        .merge(circleGroupEnter)
        .attr('transform', `translate(${cx},${cy})`);

      // Grid lines
      const gridGroup = svg.selectAll('g.grid').data([null]);
      const gridGroupEnter = gridGroup
        .enter()
        .append('g')
        .classed('grid', true)
        .attr('transform', `translate(${cx},${cy})`);

      const gridGroupAll = gridGroup
        .merge(gridGroupEnter)
        .attr('transform', `translate(${cx},${cy})`);

      if (currentGridType.value === 'none') {
        gridGroupAll.selectAll('circle.ring-boundary').remove();
        gridGroupAll.selectAll('path.radial-line').remove();
        gridGroupAll.selectAll('path.circumferential-line').remove();
      }
      else if (currentGridType.value === 'aha') {
        drawAHAGrid(gridGroupAll, outerR, innerR);
      }
      else if (currentGridType.value === 'territories') {
        drawTerritoriesGrid(gridGroupAll, outerR, innerR);
      }
      else {
        drawDataGrid(gridGroupAll, outerR, innerR);
      }

      // Inner circle
      circleGroupAll
        .selectAll('circle.inner')
        .data([innerR])
        .join('circle')
        .classed('inner', true)
        .attr('r', d => d)
        .attr('fill', 'none')
        .attr('stroke', 'white')
        .attr('stroke-width', 1);

      // Outer circle
      circleGroupAll
        .selectAll('circle.outer')
        .data([outerR])
        .join('circle')
        .classed('outer', true)
        .attr('r', d => d)
        .attr('fill', 'none')
        .attr('stroke', 'white')
        .attr('stroke-width', 1);

      // Start marker
      const startRad = ((startAngleOffset.value - 90) * Math.PI) / 180;
      const markerR = outerR;
      const markerX = markerR * Math.cos(startRad);
      const markerY = markerR * Math.sin(startRad);

      circleGroupAll
        .selectAll('circle.marker')
        .data([{ x: markerX, y: markerY }])
        .join('circle')
        .classed('marker', true)
        .attr('cx', d => d.x)
        .attr('cy', d => d.y)
        .attr('r', 4)
        .attr('fill', 'white');

      // Highlight the ring band for the currently selected slice
      const R = props.ringsData.length;
      const hi = props.highlightSlice;
      if (Number.isInteger(hi) && hi >= 0 && hi < R) {
        const bandOuter = innerR + (outerR - innerR) * ((R - hi) / R);
        const bandInner = innerR + (outerR - innerR) * ((R - hi - 1) / R);
        const highlightArc = arc()
          .innerRadius(bandInner)
          .outerRadius(bandOuter)
          .startAngle(0)
          .endAngle(2 * Math.PI);

        circleGroupAll
          .selectAll('path.slice-highlight')
          .data([null])
          .join('path')
          .classed('slice-highlight', true)
          .attr('d', highlightArc)
          .attr('fill', 'rgba(255, 255, 255, 0.0)') // optional translucent band
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1);
      }
      else {
        circleGroupAll.selectAll('path.slice-highlight').remove();
      }

      // Clear the canvas before painting pixels
      ctx.clearRect(0, 0, c.width, c.height);

      const mode = currentInterpolationMode.value;
      const size = props.pixelSize;
      const hatch = makeHatchPattern(ctx);

      if (S === 0) {
        // All rings empty: hatch the whole donut so it reads as "no data"
        ctx.fillStyle = hatch;
        ctx.beginPath();
        ctx.arc(cx, cy, outerR, 0, 2 * Math.PI);
        ctx.arc(cx, cy, innerR, 0, 2 * Math.PI, true);
        ctx.fill('evenodd');
      }
      else {
        for (let yy = cy - outerR; yy < cy + outerR; yy += size) {
          for (let xx = cx - outerR; xx < cx + outerR; xx += size) {
            const px = xx + size / 2;
            const py = yy + size / 2;
            const v = computePolarmapValue(
              px,
              py,
              props.ringsData,
              innerR,
              outerR,
              cx,
              cy,
              startAngleOffset.value,
              mode,
              S,
            );
            if (v === NO_DATA) {
              ctx.fillStyle = hatch;
              ctx.fillRect(xx, yy, size, size);
            }
            else if (v !== null) {
              ctx.fillStyle = getColor(v);
              ctx.fillRect(xx, yy, size, size);
            }
          }
        }
      }

      // ----- Bottom text stack: title, description, metadata -----
      const textGroup = svg.selectAll('g.text-field').data([null]);
      const textGroupEnter = textGroup
        .enter()
        .append('g')
        .classed('text-field', true);
      const textGroupAll = textGroup.merge(textGroupEnter);

      const textX = c.width - 20; // 20px from right edge
      const baseTextY = c.height - 20; // 20px from bottom
      const lineHeight = 16;

      // compute once, before drawing the three lines
      const descriptionText = props.description
        ? `${props.description} (${props.unit})`
        : '';
      const hasDescription = Boolean(descriptionText);

      // Title + unit — sits 2 lines up only when a description occupies the middle line
      textGroupAll
        .selectAll('text.title')
        .data([null])
        .join('text')
        .classed('title', true)
        .attr('x', textX)
        .attr('y', baseTextY - (hasDescription ? 2 : 1) * lineHeight)
        .attr('text-anchor', 'end')
        .attr('fill', 'white')
        .attr('font-family', 'sans-serif')
        .each(function titleRenderer() {
          const text = select(this);
          text.text('');
          text
            .append('tspan')
            .text(props.title)
            .attr('font-size', '14px')
            .attr('font-weight', 'bold');
          text
            .append('tspan')
            .text(` (${props.unit})`)
            .attr('font-size', '10px')
            .attr('font-weight', 'normal');
        });

      textGroupAll
        .selectAll('text.description')
        .data(descriptionText ? [descriptionText] : [])
        .join('text')
        .classed('description', true)
        .attr('x', textX)
        .attr('y', baseTextY - lineHeight)
        .attr('text-anchor', 'end')
        .attr('fill', 'rgba(255,255,255,0.85)')
        .attr('font-size', '11px')
        .attr('font-family', 'sans-serif')
        .text(d => d);

      // Metadata line (slices / segments / empty count)
      const emptyCount = props.ringsData.filter(
        ring => !ring || ring.length === 0,
      ).length;
      const metadata = `Slices: ${props.ringsData.length}, Segments: ${S}${emptyCount ? `, Empty: ${emptyCount}` : ''}`;
      textGroupAll
        .selectAll('text.metadata')
        .data([metadata])
        .join('text')
        .classed('metadata', true)
        .attr('x', textX)
        .attr('y', baseTextY)
        .attr('text-anchor', 'end')
        .attr('fill', 'white')
        .attr('font-size', '12px')
        .attr('font-family', 'sans-serif')
        .text(d => d);
    };

    onMounted(() => {
      resizeObserver = new ResizeObserver(draw);
      resizeObserver.observe(canvas.value.parentElement);
      draw();
    });
    onBeforeUnmount(() => {
      resizeObserver.disconnect();
      select(canvas.value.parentElement).select('svg').remove();
    });
    watch(
      [
        () => props.ringsData,
        () => currentColormap.value,
        () => props.innerCircleRadius,
        () => currentInterpolationMode.value,
        () => props.pixelSize,
        () => props.title,
        () => props.unit,
        () => props.description,
        () => props.highlightSlice,
      ],
      draw,
      { deep: true },
    );

    watch(
      () => props.colormap,
      (newColormap) => {
        currentColormap.value = newColormap;
      },
    );

    const showContextMenu = (event) => {
      event.preventDefault();
      showMenu.value = true;
      menuStyle.value = {
        top: `${event.clientY}px`,
        left: `${event.clientX}px`,
      };

      const closeMenu = (e) => {
        if (!e.target.closest('.context-menu')) {
          showMenu.value = false;
          document.removeEventListener('click', closeMenu);
        }
      };
      document.addEventListener('click', closeMenu);
    };

    const selectMode = (mode) => {
      currentInterpolationMode.value = mode;
      showMenu.value = false;
      emit('update:interpolationMode', mode);
    };

    const selectColormap = (colormap) => {
      currentColormap.value = colormap;
      showMenu.value = false;
      emit('update:colormap', colormap);
    };

    const selectGridType = (type) => {
      currentGridType.value = type;
      draw();
    };

    const getColormapLabel = name => colormapLabels[name] || name;

    const getColormapPreviewStyle = (name) => {
      const gradientStops = Array.from({ length: 10 }, (_, i) => {
        const t = i / 9;
        return `${colormaps[name](t)} ${t * 100}%`;
      }).join(', ');

      return {
        background: `linear-gradient(to right, ${gradientStops})`,
      };
    };

    // Original data value at (r, theta); null if outside / missing
    const getOriginalValue = (r, theta) => {
      const firstNonEmpty = firstNonEmptyIndex(props.ringsData);
      if (firstNonEmpty === -1) return null;
      const S = props.ringsData[firstNonEmpty].length;
      const R = props.ringsData.length;
      if (S === 0 || R === 0) return null;

      const segF = (theta / (2 * Math.PI)) * S;
      const si = Math.floor(segF) % S;
      const ri = Math.min(R - 1, Math.max(0, Math.floor((1 - r) * R)));
      const value = cell(props.ringsData, ri, si);
      if (isMissing(value)) return null;
      return { value, ri, si };
    };

    const hideTooltip = () => {
      showTooltip.value = false;
    };

    const handleMouseMove = (event) => {
      const rect = canvas.value.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const cx = (canvas.value.width - paddingX.value) / 2;
      const cy = (canvas.value.height - paddingY.value) / 2;
      const outerR = Math.min(cx, cy);
      const innerR = outerR * props.innerCircleRadius;

      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy);

      if (r < innerR || r > outerR) {
        hideTooltip();
        return;
      }

      if (props.ringsData.length === 0) {
        hideTooltip();
        return;
      }

      const rNorm = (r - innerR) / (outerR - innerR);

      let theta = Math.atan2(dx, -dy);
      theta -= (startAngleOffset.value * Math.PI) / 180;
      theta = ((theta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

      const result = getOriginalValue(rNorm, theta);

      // No tooltip over empty rings / missing cells
      if (result === null || !Number.isFinite(result.value)) {
        hideTooltip();
        return;
      }

      tooltipValue.value = result.value;
      tooltipSlice.value = result.ri + 1;
      tooltipSegment.value = result.si + 1;
      tooltipStyle.value = {
        left: `${event.clientX + 10}px`,
        top: `${event.clientY + 10}px`,
      };
      showTooltip.value = true;
    };

    const imageClipboardStore = useImageClipboardStore();
    const screenshotDialog = ref(false);
    const screenshotDataUrl = ref('');
    const screenshotDimensions = ref({ width: 0, height: 0 });

    const drawGridOnCanvasContext = (cCtx, cx, cy, outerR, innerR) => {
      cCtx.strokeStyle = 'rgba(255, 255, 255, 0.3)';

      cCtx.lineWidth = 2;

      if (currentGridType.value === 'aha') {
        const ringRadii = [
          innerR,
          innerR + (outerR - innerR) * 0.33,
          innerR + (outerR - innerR) * 0.66,
          outerR,
        ];

        ringRadii.forEach((r) => {
          cCtx.beginPath();
          cCtx.arc(cx, cy, r, 0, 2 * Math.PI);
          cCtx.stroke();
        });

        for (let i = 0; i < 6; i++) {
          const angle = (i * 2 * Math.PI) / 6 - Math.PI / 2 - Math.PI / 6;
          cCtx.beginPath();
          cCtx.moveTo(
            cx + ringRadii[2] * Math.cos(angle),
            cy + ringRadii[2] * Math.sin(angle),
          );
          cCtx.lineTo(
            cx + outerR * Math.cos(angle),
            cy + outerR * Math.sin(angle),
          );
          cCtx.stroke();

          cCtx.beginPath();
          cCtx.moveTo(
            cx + ringRadii[1] * Math.cos(angle),
            cy + ringRadii[1] * Math.sin(angle),
          );
          cCtx.lineTo(
            cx + ringRadii[2] * Math.cos(angle),
            cy + ringRadii[2] * Math.sin(angle),
          );
          cCtx.stroke();
        }

        for (let i = 0; i < 4; i++) {
          const angle = (i * 2 * Math.PI) / 4 - Math.PI / 2 - Math.PI / 4;
          cCtx.beginPath();
          cCtx.moveTo(
            cx + innerR * Math.cos(angle),
            cy + innerR * Math.sin(angle),
          );
          cCtx.lineTo(
            cx + ringRadii[1] * Math.cos(angle),
            cy + ringRadii[1] * Math.sin(angle),
          );
          cCtx.stroke();
        }
      }
      else if (currentGridType.value === 'territories') {
        const oneThirdR = innerR + (outerR - innerR) * 0.333;

        const oneOClock = -Math.PI / 3;
        const fiveOClock = Math.PI / 3;
        const nineOClock = -Math.PI;
        const oneThirty = -Math.PI / 4;
        const sevenThirty = (-5 * Math.PI) / 4;
        const fourThirty = Math.PI / 4;

        const radialLines = [
          { start: nineOClock, r0: oneThirdR, r1: outerR },
          { start: sevenThirty, r0: innerR, r1: oneThirdR },
          { start: oneOClock, r0: oneThirdR, r1: outerR },
          { start: oneThirty, r0: innerR, r1: oneThirdR },
          { start: fiveOClock, r0: oneThirdR, r1: outerR },
          { start: fourThirty, r0: innerR, r1: oneThirdR },
        ];

        radialLines.forEach((lineData) => {
          cCtx.beginPath();
          cCtx.moveTo(
            cx + lineData.r0 * Math.cos(lineData.start),
            cy + lineData.r0 * Math.sin(lineData.start),
          );
          cCtx.lineTo(
            cx + lineData.r1 * Math.cos(lineData.start),
            cy + lineData.r1 * Math.sin(lineData.start),
          );
          cCtx.stroke();
        });

        cCtx.beginPath();
        cCtx.arc(cx, cy, oneThirdR, fourThirty, fiveOClock);
        cCtx.stroke();

        cCtx.beginPath();
        cCtx.arc(cx, cy, oneThirdR, oneOClock, oneThirty);
        cCtx.stroke();

        cCtx.beginPath();
        cCtx.arc(cx, cy, oneThirdR, sevenThirty, nineOClock);
        cCtx.stroke();
      }
      else if (currentGridType.value === 'data') {
        const S = props.ringsData[0].length;
        const R = props.ringsData.length;

        for (let i = 0; i <= R; i++) {
          const r = innerR + (outerR - innerR) * (i / R);
          cCtx.beginPath();
          cCtx.arc(cx, cy, r, 0, 2 * Math.PI);
          cCtx.stroke();
        }

        const angleStep = (2 * Math.PI) / S;
        for (let i = 0; i < S; i++) {
          const angle = i * angleStep;
          cCtx.beginPath();
          cCtx.moveTo(
            cx + innerR * Math.cos(angle),
            cy + innerR * Math.sin(angle),
          );
          cCtx.lineTo(
            cx + outerR * Math.cos(angle),
            cy + outerR * Math.sin(angle),
          );
          cCtx.stroke();
        }
      }

      cCtx.strokeStyle = 'white';

      cCtx.lineWidth = 1;
      cCtx.beginPath();
      cCtx.arc(cx, cy, innerR, 0, 2 * Math.PI);
      cCtx.stroke();

      cCtx.beginPath();
      cCtx.arc(cx, cy, outerR, 0, 2 * Math.PI);
      cCtx.stroke();

      const startRad = ((startAngleOffset.value - 90) * Math.PI) / 180;
      const markerX = cx + outerR * Math.cos(startRad);
      const markerY = cy + outerR * Math.sin(startRad);

      cCtx.fillStyle = 'white';
      cCtx.beginPath();
      cCtx.arc(markerX, markerY, 4, 0, 2 * Math.PI);
      cCtx.fill();
    };

    const takeScreenshot = () => {
      const c = canvas.value;
      if (!c) return;

      const displayOuterR = Math.min(
        (c.width - paddingX.value) / 2,
        (c.height - paddingY.value) / 2,
      );
      const outerR = Math.max(displayOuterR, 200);
      const innerR = outerR * props.innerCircleRadius;
      const margin = 12;
      const size = Math.ceil((outerR + margin) * 2);

      const offscreen = document.createElement('canvas');
      offscreen.width = size;
      offscreen.height = size;
      const offCtx = offscreen.getContext('2d');

      const cx = size / 2;
      const cy = size / 2;

      const firstNonEmpty = firstNonEmptyIndex(props.ringsData);
      const S = firstNonEmpty === -1 ? 0 : props.ringsData[firstNonEmpty].length;
      const mode = currentInterpolationMode.value;
      const pixelSize = props.pixelSize;
      const hatch = makeHatchPattern(offCtx);

      if (S === 0) {
        offCtx.fillStyle = hatch;
        offCtx.beginPath();
        offCtx.arc(cx, cy, outerR, 0, 2 * Math.PI);
        offCtx.arc(cx, cy, innerR, 0, 2 * Math.PI, true);
        offCtx.fill('evenodd');
      }
      else {
        for (let yy = cy - outerR; yy < cy + outerR; yy += pixelSize) {
          for (let xx = cx - outerR; xx < cx + outerR; xx += pixelSize) {
            const px = xx + pixelSize / 2;
            const py = yy + pixelSize / 2;
            const v = computePolarmapValue(
              px,
              py,
              props.ringsData,
              innerR,
              outerR,
              cx,
              cy,
              startAngleOffset.value,
              mode,
              S,
            );
            if (v === NO_DATA) {
              offCtx.fillStyle = hatch;
              offCtx.fillRect(xx, yy, pixelSize, pixelSize);
            }
            else if (v !== null) {
              offCtx.fillStyle = getColor(v);
              offCtx.fillRect(xx, yy, pixelSize, pixelSize);
            }
          }
        }
      }

      drawGridOnCanvasContext(offCtx, cx, cy, outerR, innerR);

      const R = props.ringsData.length;
      const hi = props.highlightSlice;
      if (Number.isInteger(hi) && hi >= 0 && hi < R) {
        const bandOuter = innerR + (outerR - innerR) * ((R - hi) / R);
        const bandInner = innerR + (outerR - innerR) * ((R - hi - 1) / R);
        offCtx.strokeStyle = 'white';
        offCtx.lineWidth = 1;
        offCtx.beginPath();
        offCtx.arc(cx, cy, bandOuter, 0, 2 * Math.PI);
        offCtx.arc(cx, cy, bandInner, 0, 2 * Math.PI);
        offCtx.stroke();
      }

      const imageURL = offscreen.toDataURL('image/png');
      screenshotDataUrl.value = imageURL;
      screenshotDimensions.value = {
        width: size,
        height: size,
      };
      screenshotDialog.value = true;
    };

    const handleScreenshotSave = (screenshotData) => {
      const clipboardImage = {
        imageDataURL: screenshotData.imageDataURL,
        title: screenshotData.title,
        description: screenshotData.description,
        tags: screenshotData.tags,
        viewportId: 'Polarmap',
        width: screenshotDimensions.value.width,
        height: screenshotDimensions.value.height,
        colorbarInfo: screenshotData.colorbarInfo,
      };
      imageClipboardStore.addImage(clipboardImage);
    };

    const handleScreenshotDiscard = () => {
      screenshotDataUrl.value = '';
    };

    const screenshotImageInfo = computed(() => {
      return {
        imageDataURL: screenshotDataUrl.value,
        title: `${props.title} (${props.unit})`,
        defaultTitle: props.title,
        type: 'polarmap',
        colorbarInfo: {
          colormap: currentColormap.value,
          minValue: minValue.value,
          maxValue: maxValue.value,
        },
      };
    });

    return {
      canvas,
      currentInterpolationMode,
      currentColormap,
      currentGridType,
      showMenu,
      menuStyle,
      showContextMenu,
      selectMode,
      selectColormap,
      selectGridType,
      interpolationModes,
      colormaps,
      gridTypes,
      getColormapLabel,
      getColormapPreviewStyle,
      showTooltip,
      tooltipValue,
      tooltipSlice,
      tooltipSegment,
      tooltipStyle,
      handleMouseMove,
      hideTooltip,
      meanMinus2Std,
      takeScreenshot,
      screenshotDialog,
      screenshotImageInfo,
      handleScreenshotSave,
      handleScreenshotDiscard,
    };
  },
};
</script>

<template>
  <div class="polar-map-container" @contextmenu.prevent="showContextMenu">
    <div v-if="showTooltip" class="tooltip" :style="tooltipStyle">
      Value: {{ tooltipValue.toFixed(2) }} {{ unit }}
      <div class="tooltip-sub">
        Slice {{ tooltipSlice }}, Segment {{ tooltipSegment }}
      </div>
    </div>
    <div v-if="showMenu" class="context-menu" :style="menuStyle">
      <div class="menu-group submenu-trigger">
        <div class="menu-title">
          Interpolation Mode ▶
        </div>
        <div class="submenu">
          <div
            v-for="mode in interpolationModes" :key="mode.value" class="menu-item"
            :class="{ active: currentInterpolationMode === mode.value }" @click="selectMode(mode.value)"
          >
            {{ mode.label }}
          </div>
        </div>
      </div>

      <div class="menu-separator" />

      <div class="menu-group submenu-trigger">
        <div class="menu-title">
          Grid Type ▶
        </div>
        <div class="submenu">
          <div
            v-for="(label, type) in gridTypes"
            :key="type"
            class="menu-item"
            :class="{ active: currentGridType === type }"
            @click="selectGridType(type)"
          >
            {{ label }}
          </div>
        </div>
      </div>

      <div class="menu-separator" />

      <div class="menu-group submenu-trigger">
        <div class="menu-title">
          Color Map ▶
        </div>
        <div class="submenu">
          <div
            v-for="(fn, name) in colormaps"
            :key="name"
            class="menu-item colormap-item"
            :class="{ active: currentColormap === name }"
            @click="selectColormap(name)"
          >
            <div
              class="colormap-preview"
              :style="getColormapPreviewStyle(name)"
            />
            {{ getColormapLabel(name) }}
          </div>
        </div>
      </div>
    </div>
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
    <canvas
      ref="canvas"
      class="pixel-donut-canvas"
      @mousemove="handleMouseMove"
      @mouseleave="hideTooltip"
    />

    <!-- Screenshot Button -->
    <div class="polar-map-screenshot-btn">
      <v-btn
        icon
        size="x-small"
        variant="text"
        color="white"
        @click="takeScreenshot"
      >
        <v-icon size="small">
          mdi-camera
        </v-icon>
        <v-tooltip location="bottom" activator="parent">
          Take Screenshot
        </v-tooltip>
      </v-btn>
    </div>

    <!-- Screenshot Dialog -->
    <ScreenshotDialog
      :is-open="screenshotDialog"
      :image-info="screenshotImageInfo"
      @update:dialog-open="screenshotDialog = $event"
      @save="handleScreenshotSave"
      @discard="handleScreenshotDiscard"
    />
  </div>
</template>

<style scoped>
.polar-map-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.polar-map-screenshot-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}

.controls {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 4px;
  z-index: 1;
}

.interpolation-select {
  margin-left: 8px;
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.pixel-donut-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.context-menu {
  position: fixed;
  background: var(--md-surface);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 4px 0;
  min-width: 180px;
  z-index: 1000;
}

.menu-group {
  padding: 4px 0;
  position: relative;
}

.submenu-trigger {
  position: relative;
}

.submenu-trigger:hover > .submenu {
  display: block;
}

.submenu {
  display: none;
  position: absolute;
  left: 100%;
  top: 0;
  background: var(--md-surface);
  border-radius: 4px;
  min-width: 180px;
}

.menu-separator {
  height: 1px;
  background: var(--md-surface);
  margin: 4px 0;
}

.menu-title {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.menu-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.menu-item:hover {
  background: var(--md-surface-container);
}

.menu-item.active {
  background: var(--md-surface-container);
}

.colormap-item {
  padding-right: 16px;
}

.colormap-preview {
  width: 40px;
  height: 16px;
  border-radius: 2px;
}

.tooltip {
  position: fixed;
  background: var(--md-surface);
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  white-space: nowrap;
}

.tooltip-sub {
  font-size: 10px;
  opacity: 0.7;
}
</style>
